// src/Dashboard/pages/user/Galleries/hooks/useGalleriesPhotos.js

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { useUser } from "../../../../../pages/Home/UserContext";
import { getRoleFromRoleId } from "../../../../../utils/roleHelper";

import {
  deleteGallery,
  getAllGalleries,
} from "../../../../../services/gallerieServices";

import {
  ALBUMS_UI_STORAGE_KEY,
  DEFAULT_ALBUMS_UI,
  buildAlbumSearchText,
  collatorFR,
  readStorageJson,
  writeStorageJson,
} from "../utils/galleriesUtils";

export function useGalleriesPhotos() {
  const { user } = useUser();

  const userRole = getRoleFromRoleId(user?.role_id);
  const isManager = userRole === "admin" || userRole === "enseignant";

  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [ui, setUi] = useState(() => {
    const saved = readStorageJson(ALBUMS_UI_STORAGE_KEY, null);
    return saved ? { ...DEFAULT_ALBUMS_UI, ...saved } : DEFAULT_ALBUMS_UI;
  });

  const [debouncedQ, setDebouncedQ] = useState(ui.q || "");

  const [drawerMode, setDrawerMode] = useState("create"); // create | edit
  const [selectedGallery, setSelectedGallery] = useState(null);
  const [albumDrawerOpen, setAlbumDrawerOpen] = useState(false);

  const persistUi = useCallback((next) => {
    setUi(next);
    writeStorageJson(ALBUMS_UI_STORAGE_KEY, next);
  }, []);

  const updateUi = useCallback(
    (patch) => {
      persistUi({
        ...ui,
        ...patch,
      });
    },
    [persistUi, ui]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      persistUi({
        ...ui,
        q: debouncedQ,
      });
    }, 300);

    return () => clearTimeout(timer);
  }, [debouncedQ]); // volontairement limité

  const fetchGalleries = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getAllGalleries();

      setGalleries(Array.isArray(response) ? response : []);
    } catch (err) {
      console.error("Erreur chargement galeries :", err);
      setGalleries([]);
      setError("Impossible de charger les galeries pour le moment.");
      toast.error("Erreur lors du chargement des galeries.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGalleries();
  }, [fetchGalleries]);

  const filtered = useMemo(() => {
    let list = galleries.filter((gallery) => {
      if (!isManager) return !!gallery?.is_active;

      return ui.showAll ? true : !!gallery?.is_active;
    });

    const q = String(ui.q || "").trim().toLowerCase();

    if (q) {
      list = list.filter((gallery) => buildAlbumSearchText(gallery).includes(q));
    }

    list.sort((a, b) => {
      if (ui.sort === "alpha") {
        return collatorFR.compare(String(a?.titre || ""), String(b?.titre || ""));
      }

      const da =
        new Date(a?.updated_at || a?.created_at || 0).getTime() ||
        Number(a?.id) ||
        0;

      const db =
        new Date(b?.updated_at || b?.created_at || 0).getTime() ||
        Number(b?.id) ||
        0;

      if (ui.sort === "oldest") {
        return da - db || collatorFR.compare(String(a?.titre || ""), String(b?.titre || ""));
      }

      return db - da || collatorFR.compare(String(a?.titre || ""), String(b?.titre || ""));
    });

    return list;
  }, [galleries, ui, isManager]);

  const stats = useMemo(() => {
    const total = galleries.length;
    const active = galleries.filter((gallery) => gallery?.is_active).length;
    const hidden = total - active;
    const photos = galleries.reduce((sum, gallery) => {
      return sum + (Array.isArray(gallery?.photos) ? gallery.photos.length : 0);
    }, 0);

    return {
      total,
      active,
      hidden,
      photos,
      filtered: filtered.length,
    };
  }, [galleries, filtered.length]);

  const resetFilters = useCallback(() => {
    setDebouncedQ("");
    persistUi(DEFAULT_ALBUMS_UI);
  }, [persistUi]);

  const openCreateDrawer = useCallback(() => {
    setSelectedGallery(null);
    setDrawerMode("create");
    setAlbumDrawerOpen(true);
  }, []);

  const openEditDrawer = useCallback((gallery) => {
    setSelectedGallery(gallery);
    setDrawerMode("edit");
    setAlbumDrawerOpen(true);
  }, []);

  const closeAlbumDrawer = useCallback(() => {
    setAlbumDrawerOpen(false);
    setSelectedGallery(null);
    setDrawerMode("create");
  }, []);

  const handleAlbumSaved = useCallback(async () => {
    await fetchGalleries();
    closeAlbumDrawer();
  }, [fetchGalleries, closeAlbumDrawer]);

  const handleDeleteGallery = useCallback(
    async (gallery) => {
      if (!isManager || !gallery?.id) return;

      const ok = window.confirm(
        `Supprimer la galerie "${gallery?.titre || "Sans titre"}" ?`
      );

      if (!ok) return;

      try {
        await deleteGallery(gallery.id);
        toast.success("Galerie supprimée avec succès.");
        await fetchGalleries();
      } catch (err) {
        console.error("Erreur suppression galerie :", err);
        toast.error("Erreur lors de la suppression de la galerie.");
      }
    },
    [isManager, fetchGalleries]
  );

  return {
    userRole,
    isManager,

    galleries,
    filtered,
    loading,
    error,
    stats,

    ui,
    debouncedQ,
    setDebouncedQ,
    updateUi,
    resetFilters,

    drawerMode,
    selectedGallery,
    albumDrawerOpen,
    openCreateDrawer,
    openEditDrawer,
    closeAlbumDrawer,
    handleAlbumSaved,

    handleDeleteGallery,
    refresh: fetchGalleries,
  };
}